<template>
  <div
    v-if="showWarning"
    class="session-timeout-warning alert alert-warning shadow text-left"
    role="alertdialog"
    aria-live="assertive"
    aria-labelledby="session-timeout-title"
  >
    <p id="session-timeout-title" class="mb-3">
      <font-awesome-icon icon="exclamation-triangle" class="mr-1" />
      {{ $t("sessionTimeout.inactiveTitle") }}
    </p>
    <i18n path="sessionTimeout.logoutIn" tag="p" class="mb-3">
      <template v-slot:time>
        <strong>{{ remainingText }}</strong>
      </template>
    </i18n>
    <b-button variant="primary" @click="onStayLoggedIn">
      {{ $t("sessionTimeout.stayLoggedIn") }}
    </b-button>
  </div>
</template>

<script>
import config from "@/configs/config";
import { HTTP } from "@/utils/HTTP";
import { Helper } from "@/helpers";
import { refreshAccessToken, clearLocalSession } from "@/utils/auth-requests";
import { EventBus } from "@/event-bus.js";

// Shared across tabs so activity in any tab keeps the whole session alive
const LAST_ACTIVITY_KEY = "sessionLastActivity";
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "wheel", "touchstart", "scroll"];
const CHECK_INTERVAL_MS = 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 1000;
// The API expires the refresh token after the same idle timeout, so while the user is active
// (even without making API calls, e.g. filling in a long form) keep the server session sliding
const SERVER_KEEP_ALIVE_MS = 30 * 1000;

export default {
  name: "SessionTimeout",
  data () {
    return {
      timeoutMs: config.SESSION_TIMEOUT_MINUTES * 60 * 1000,
      warningMs: config.SESSION_WARNING_MINUTES * 60 * 1000,
      showWarning: false,
      remainingMs: 0,
      wasAuthenticated: false,
      isLoggingOut: false,
      lastActivityWrite: 0,
      lastServerKeepAlive: 0,
      checkInterval: null,
    };
  },
  computed: {
    remainingText () {
      let seconds = Math.max( 0, Math.ceil( this.remainingMs / 1000 ) );
      if ( seconds < 60 ) {
        return this.$tc( "sessionTimeout.seconds", seconds, { n: seconds } );
      }
      let minutes = Math.ceil( seconds / 60 );
      return this.$tc( "sessionTimeout.minutes", minutes, { n: minutes } );
    },
  },
  mounted () {
    ACTIVITY_EVENTS.forEach( ( eventName ) => {
      document.addEventListener( eventName, this.onActivity, { capture: true, passive: true } );
    } );
    // Background tabs throttle timers, so re-check as soon as the user comes back
    document.addEventListener( "visibilitychange", this.checkSession );
    window.addEventListener( "focus", this.checkSession );
    EventBus.$on( "sessionExpired", this.onSessionExpired );
    this.checkInterval = setInterval( this.checkSession, CHECK_INTERVAL_MS );
    this.checkSession();
  },
  beforeDestroy () {
    ACTIVITY_EVENTS.forEach( ( eventName ) => {
      document.removeEventListener( eventName, this.onActivity, { capture: true, passive: true } );
    } );
    document.removeEventListener( "visibilitychange", this.checkSession );
    window.removeEventListener( "focus", this.checkSession );
    EventBus.$off( "sessionExpired", this.onSessionExpired );
    clearInterval( this.checkInterval );
  },
  methods: {
    hasAccessToken () {
      // localStorage is shared by all tabs (and is what HTTP.js sends), so it's the source of truth
      return !!localStorage.getItem( "accessToken" );
    },
    isProtectedRoute () {
      return !!( this.$route && this.$route.meta && this.$route.meta.jwtRequiresAuth );
    },
    getLastActivity () {
      let lastActivity = parseInt( localStorage.getItem( LAST_ACTIVITY_KEY ), 10 );
      return Number.isFinite( lastActivity ) ? lastActivity : null;
    },
    setLastActivity ( timestamp ) {
      this.lastActivityWrite = timestamp;
      localStorage.setItem( LAST_ACTIVITY_KEY, String( timestamp ) );
    },
    onActivity () {
      if ( this.isLoggingOut || !this.hasAccessToken() || !this.isProtectedRoute() ) {
        return;
      }
      let now = Date.now();
      if ( now - this.lastActivityWrite < ACTIVITY_WRITE_THROTTLE_MS ) {
        return;
      }
      // Once the warning period has started (or the session has already expired, e.g. after
      // the machine slept) only the "Stay Logged In" button may extend the session
      let lastActivity = this.getLastActivity();
      if ( lastActivity !== null && now - lastActivity >= this.timeoutMs - this.warningMs ) {
        this.checkSession();
        return;
      }
      this.setLastActivity( now );
      this.keepServerSessionAlive( now );
    },
    keepServerSessionAlive ( now ) {
      if ( now - this.lastServerKeepAlive < SERVER_KEEP_ALIVE_MS ) {
        return;
      }
      this.lastServerKeepAlive = now;
      // Fails when the server session has ended (idle timeout, or the password was changed or
      // reset on another device)
      refreshAccessToken( this.$store ).then( ( accessToken ) => {
        if ( accessToken === false ) {
          this.endSession( "sessionTimeout.sessionEnded" );
        }
      } ).catch( () => {} );
    },
    onSessionExpired () {
      // Several requests can fail together; only act while still logged in
      if ( this.hasAccessToken() ) {
        this.endSession( "sessionTimeout.sessionEnded" );
      }
    },
    checkSession () {
      if ( this.isLoggingOut ) {
        return;
      }
      if ( !this.hasAccessToken() ) {
        // Not logged in: drop any stale timestamp so the next login starts a fresh idle window
        localStorage.removeItem( LAST_ACTIVITY_KEY );
        this.showWarning = false;
        // Another tab logged out (or timed out) — don't leave this tab showing protected data
        if ( this.wasAuthenticated && this.isProtectedRoute() ) {
          this.wasAuthenticated = false;
          this.$store.commit( "SET_ACCESS_TOKEN", null );
          this.$router.replace( "/" ).catch( () => {} );
        }
        this.wasAuthenticated = false;
        return;
      }
      this.wasAuthenticated = true;
      if ( !this.isProtectedRoute() ) {
        this.showWarning = false;
        return;
      }
      let now = Date.now();
      let lastActivity = this.getLastActivity();
      if ( lastActivity === null ) {
        this.setLastActivity( now );
        lastActivity = now;
      }
      let idleMs = now - lastActivity;
      if ( idleMs >= this.timeoutMs ) {
        this.endSession( "sessionTimeout.loggedOut" );
      } else if ( idleMs >= this.timeoutMs - this.warningMs ) {
        this.remainingMs = this.timeoutMs - idleMs;
        this.showWarning = true;
      } else {
        this.showWarning = false;
      }
    },
    async onStayLoggedIn () {
      let now = Date.now();
      this.setLastActivity( now );
      this.showWarning = false;
      this.lastServerKeepAlive = now;
      let accessToken = false;
      try {
        accessToken = await refreshAccessToken( this.$store );
      } catch ( error ) {
        // Network error: keep the user logged in, the next request will retry the refresh
        return;
      }
      if ( accessToken === false ) {
        // The server session expired before the button was clicked
        this.endSession( "sessionTimeout.loggedOut" );
      }
    },
    endSession ( messageKey ) {
      if ( this.isLoggingOut ) {
        return;
      }
      this.isLoggingOut = true;
      this.showWarning = false;
      // Revoke the refresh token server side so the session can't be resumed. The access
      // token is read from localStorage synchronously, before it is cleared below
      HTTP( "get", "logout", null ).catch( () => {} );
      clearLocalSession( this.$store );
      this.wasAuthenticated = false;
      Helper.makeToast(
        this.$bvToast,
        "warning",
        this.$t( messageKey ),
        this.$t( "toast.title" )
      );
      this.$router.replace( "/" ).catch( () => {} ).finally( () => {
        this.isLoggingOut = false;
      } );
    },
  },
};
</script>

<style scoped>
.session-timeout-warning {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 1090;
  width: 340px;
  max-width: calc(100vw - 32px);
  padding: 20px;
}
</style>
